import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { InputTextarea } from "primereact/inputtextarea";
import { useTranslation } from "react-i18next";

import {
    useEmergencyStatus,
    useUpdateEmergencySettings,
} from "../../../queries/emergency";

const DEFAULT_MESSAGE = "سنعود قريباً / We'll be back shortly";

const EmergencySettings = () => {
    const { t } = useTranslation();
    const { data, isLoading } = useEmergencyStatus();
    const update = useUpdateEmergencySettings();

    const [message, setMessage] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        if (data) {
            setMessage(data.message || DEFAULT_MESSAGE);
            setLogoPreview(data.logo_url || null);
        }
    }, [data?.message, data?.logo_url]);

    const onPickLogo = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const onClearLogo = async () => {
        await update.mutateAsync({ message, clearLogo: true });
        setLogoFile(null);
        setLogoPreview(null);
    };

    const onSave = async () => {
        await update.mutateAsync({ message, logoFile });
        setLogoFile(null);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
            <div className="space-y-4">
                <div>
                    <h3 className="font-bold text-lg mb-1">{t("emergency.settings_title")}</h3>
                    <p className="text-sm text-gray-600">
                        {t("emergency.settings_description")}
                    </p>
                </div>

                <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                        {t("emergency.default_message_label")}
                    </label>
                    <InputTextarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={2}
                        autoResize
                        className="w-full"
                        disabled={isLoading}
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                        {t("emergency.logo_label")} <span className="font-normal normal-case text-gray-400">{t("emergency.logo_optional_hint")}</span>
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onPickLogo}
                            className="text-sm"
                        />
                        {data?.logo_url && (
                            <Button
                                label={t("emergency.use_company_logo")}
                                className="p-button-text p-button-sm"
                                onClick={onClearLogo}
                                disabled={update.isLoading}
                            />
                        )}
                    </div>
                </div>

                <div className="pt-2">
                    <Button
                        label={update.isLoading ? t("emergency.saving") : t("emergency.save_settings")}
                        icon="pi pi-save"
                        className="p-button-primary"
                        onClick={onSave}
                        disabled={update.isLoading}
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                    {t("emergency.preview_label")}
                </label>
                <div
                    className="rounded-lg flex flex-col items-center justify-center"
                    style={{
                        background: "#0f172a",
                        color: "#f8fafc",
                        aspectRatio: "16 / 9",
                        padding: "2rem",
                        minHeight: "260px",
                    }}
                >
                    {logoPreview ? (
                        <img
                            src={logoPreview}
                            alt={t("emergency.logo_alt")}
                            style={{ maxWidth: "40%", maxHeight: "55%", objectFit: "contain" }}
                        />
                    ) : (
                        <div className="text-2xl font-bold opacity-80">
                            {data?.company_name || t("emergency.your_company")}
                        </div>
                    )}
                    <div
                        className="mt-6 text-center"
                        style={{ fontSize: "1.5rem", fontWeight: 500, lineHeight: 1.4 }}
                    >
                        {message || DEFAULT_MESSAGE}
                    </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    {t("emergency.preview_hint")}
                </p>
            </div>
        </div>
    );
};

export default EmergencySettings;
